import { useState, createContext, useEffect, useContext } from 'react';
import { fetchWithAccessTokenRetry } from '../helperFunctions.jsx';
import { message } from 'antd';

import ProductsPage from './ProductsPage/ProductsPage.jsx';
import CompaniesPage from './CompaniesPage/CompaniesPage.jsx';
import Dashboard from './DashboardOverview/Dashboard.jsx';

import '../CriticalFinance/PrenumbersCFI.css';
import '../CriticalFinance/EntryIndustry.css';
import '../CriticalFinance/CriticalFinanceTable.css'

export const FinancialBuilderContext = createContext(null);

/* USES CLASSES SUCH AS form-input from PreNumbersCFI.css*/

//


function PageSwitcher() {
    const { pageNum } = useContext(FinancialBuilderContext);

    switch (pageNum) {
        case 1: return <CompaniesPage />;
        case 2: return <ProductsPage />;
        case 3: return <Dashboard />;
        default: return <CompaniesPage />;
    }
}

async function getAllUserCompanies(setCompanies, setSpotRates, messageApi) {
    messageApi.info('Loading companies...');
    try {
        const response = await fetchWithAccessTokenRetry('/api/fb/getCompanies');
        if (!response.ok) {
            throw new Error('Failed to fetch companies');
        }
        const data = await response.json();
        setCompanies(data.companies);
        setSpotRates(data.spotRates);
        messageApi.destroy();
        messageApi.success('Companies loaded successfully');
    } catch (error) {
        console.error('Error fetching user companies:', error);
        setCompanies([]); // Clear companies on error
        messageApi.destroy();
        messageApi.error('Failed to load companies');
    }
}

// Get all the companies from the database and set them in the context and use them throughout the Financial Builder
/*
const companiesSampleData = [
    {
        user_id: 1,
        id: 2,
        name: "Company A",
        industry: "Technology",
        in_currency: "INR",
        out_currency: "USD",
        labor_cost: 50000,
        avg_salary: 60000,
        cross_selling_ars: 0.2,
        fixed_costs: [
            {
                id: 1,
                user_id: 1,
                companies_id: 1,
                name: "Office Rent",
                years: [
                    {
                        id: 1,
                        user_id: 1,
                        companies_id: 1,
                        fixed_costs_id: 1,
                        amount: 1000,
                        year: 0,
                    }
                ], 
            },
            {
                id: 2,
                user_id: 1,
                companies_id: 1,
                name: "Utilities",
                years: [500, 600, 700, 800, 900],
            }
        ],
        products: [
            {
                id: 1,
                user_id: 1,
                companies_id: 1,
                market_size: 1000000,
                revenue_model: "Subscription",
                market_capture: 0.1,
                product_name: "Product A1",
                product_description: "Description of Product A1",
                growth_rate: 0.05,
                growth_adjustment: 0.02,
                churn_rate: 0.01,
                annual_price: 100,
                product_id: 1,
                sales_marketing_of_revenue: 0.1,
                adjust_for_eff_and_automation: 0.05,
            }
        ]
    },
    {
        user_id: 1,
        id: 1,
        name: "Company A",
        industry: "Technology",
        in_currency: "INR",
        out_currency: "USD",
        labor_cost: 50000,
        avg_salary: 60000,
        cross_selling_ars: 0.2,
        fixed_costs: [
            {
                id: 1,
                user_id: 1,
                companies_id: 1,
                name: "Office Rent",
                years: [
                    {
                        id: 1,
                        user_id: 1,
                        companies_id: 1,
                        fixed_costs_id: 1,
                        amount: 1000,
                        year: 0,
                    }
                ], 
            },
            {
                id: 2,
                user_id: 1,
                companies_id: 1,
                name: "Utilities",
                years: [500, 600, 700, 800, 900],
            }
        ],
        products: [
            {
                id: 1,
                user_id: 1,
                companies_id: 1,
                market_size: 1000000,
                revenue_model: "Subscription",
                market_capture: 0.1,
                product_name: "Product A1",
                product_description: "Description of Product A1",
                growth_rate: 0.05,
                growth_adjustment: 0.02,
                churn_rate: 0.01,
                annual_price: 100,
                product_id: 1,
                sales_marketing_of_revenue: 0.1,
                adjust_for_eff_and_automation: 0.05,
            }
        ]
    }
]
*/

function FinancialBuilderPage() {
    const [pageNum, setPageNum] = useState(1);
    const [messageApi, contextHolder] = message.useMessage();
    const [companies, setCompanies] = useState([]);
    // This state is used to track the selected company for navigation so in other pages it can be used to add more products
    const [companySelectedID, setCompanySelectedID] = useState("");
    const [companySelected, setCompanySelected] = useState(null);
    const [spotRates, setSpotRates] = useState(null);

    // adds a single updater that changes companySelected and companies atomically
    const updateCompany = (updater) => {
        setCompanySelected(prev => {
            const next = typeof updater === 'function' ? updater(prev) : { ...prev, ...updater };
            setCompanies(prevCompanies => {
                const idx = prevCompanies.findIndex(e => e.id === next.id);
                if (idx === -1) return prevCompanies;
                const nextCompanies = [...prevCompanies];
                nextCompanies[idx] = next;
                return nextCompanies;
            });
            return next;
        });
    };


    const contextToUse = {
        pageNum,
        setPageNum,
        messageApi,
        companies,
        setCompanies,
        companySelectedID,
        setCompanySelectedID,
        companySelected,
        setCompanySelected : updateCompany,
        spotRates,
    };

    useEffect(() => {
        getAllUserCompanies(setCompanies, setSpotRates, messageApi);
    }, [messageApi]);

    // Update the selected company when the companySelectedID changes
    useEffect(() => {
        if (companySelectedID !== "-- Select a company --") {
            const selectedCompany = companies.find(company => company.id === parseInt(companySelectedID));
            setCompanySelected(selectedCompany);
        }
    }, [companySelectedID, companies]);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, [pageNum])

    return (
        <>
            {contextHolder}
            <FinancialBuilderContext.Provider value={contextToUse}>
                <PageSwitcher />
            </FinancialBuilderContext.Provider>
        </>
    );
}

export default FinancialBuilderPage;